import { observer } from "mobx-react";
import "./rma-list-filters-sidebar.scss";
import { Button } from "../../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../../components/inputs/button-variants";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { FEATURES } from "../../../../../models/Plan";
import currentPortalStore from "../../../../../stores/current-portal-store";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";
import RmaListFiltersStore from "../../../rma-list-page/stores/rma-list-filters-store";
import rmaPagedListStore from "../../stores/rma-tabs-paged-list-store";
import { RmaListFilterPopoverGroup } from "../rma-list-filter-control/rma-list-filter-popover-group";
import RmaListFilterDateRange from "../rma-list-filter-date-range/rma-list-filter-date-range";
import RmaListFilterReservedItem from "../rma-list-filter-reserved-item/rma-list-filter-reserved-item";
import RmaListFilterResolution from "../rma-list-filter-resolution/rma-list-filter-resolution";
import RmaListFilterReturnReason from "../rma-list-filter-return-reason/rma-list-filter-return-reason";
import RmaListFilterValidated from "../rma-list-filter-validated/rma-list-filter-validated";
import RmaListQuickFiltersTabs from "../rma-list-quick-filters-tabs/rma-list-quick-filters-tabs";
import RmaListActionsStatusFilter from "../rma-list-actions-status-filter/rma-list-actions-status-filter";
import RmaListFiltersSidebarClearAll from "./rma-list-filters-sidebar-clear-all/rma-list-filters-sidebar-clear-all";
import RmaListFiltersSidebarTitle from "./rma-list-filters-sidebar-title/rma-list-filters-sidebar-title";

interface IProps {
    rmaListFiltersStore: RmaListFiltersStore;
    disabled: boolean;
    onFiltersApplied?: () => void;
}

const RmaListFiltersSidebar: React.FC<IProps> = observer(
    ({ rmaListFiltersStore, disabled, onFiltersApplied }) => {
        const { getTranslation } = useDashboardTranslation();
        const scope = "dashboard-returns-page";
        const draftCount = rmaListFiltersStore.draftWiredSidebarFilterCount;
        const isReserveItemsAllowed = currentPortalStore.isFeatureAllowed(FEATURES.RESERVE_ITEMS);
        const applyLabel =
            draftCount > 0
                ? `${getTranslation("MANAGE_RETURNS_APPLY_FILTERS", scope)} (${draftCount})`
                : getTranslation("MANAGE_RETURNS_APPLY_FILTERS", scope);
        const canApply =
            draftCount > 0 && rmaListFiltersStore.reserveItemExpirationFilterValid;

        const onApplyFilters = () => {
            const status = rmaListFiltersStore.selectedQuickFilter;
            rmsMixpanelStore.trackEvent("d&_filters_apply");
            if (status) {
                rmsMixpanelStore.trackEvent(`d&_filter_${status}`, []);
            }
            rmaPagedListStore.loadPage(1);
            onFiltersApplied?.();
        };

        // Manual actions apply immediately on change. We reload the list but keep the (tablet) drawer
        // open so the merchant can add/remove more buckets without it closing on every toggle.
        const onApplyManualActions = () => {
            rmsMixpanelStore.trackEvent("d&_manual_actions_apply");
            rmaPagedListStore.loadPage(1);
        };

        return (
            <aside className="rma-list-filters-sidebar">
                <RmaListActionsStatusFilter
                    rmaListFiltersStore={rmaListFiltersStore}
                    disabled={disabled}
                    onApply={onApplyManualActions}
                />
                <div className="rma-list-filters-sidebar__header">
                    <RmaListFiltersSidebarTitle />
                    <RmaListFiltersSidebarClearAll
                        rmaListFiltersStore={rmaListFiltersStore}
                        disabled={disabled}
                        onFiltersApplied={onFiltersApplied}
                    />
                </div>
                <div className="rma-list-filters-sidebar__body">
                    <RmaListQuickFiltersTabs rmaListFiltersStore={rmaListFiltersStore} disabled={disabled} />
                    <RmaListFilterPopoverGroup>
                        <div className="rma-list-filters-sidebar__controls">
                            <RmaListFilterDateRange rmaListFiltersStore={rmaListFiltersStore} disabled={disabled} />
                            <RmaListFilterResolution rmaListFiltersStore={rmaListFiltersStore} disabled={disabled} />
                            <RmaListFilterReturnReason rmaListFiltersStore={rmaListFiltersStore} disabled={disabled} />
                            <RmaListFilterValidated rmaListFiltersStore={rmaListFiltersStore} disabled={disabled} />
                            {isReserveItemsAllowed &&
                                <RmaListFilterReservedItem
                                    rmaListFiltersStore={rmaListFiltersStore}
                                    disabled={disabled}
                                />
                            }
                        </div>
                    </RmaListFilterPopoverGroup>
                </div>
                <div className="rma-list-filters-sidebar__footer">
                    <Button
                        variant={BUTTON_VARIANTS.PrimaryAccent}
                        text={applyLabel}
                        disabled={disabled || !canApply}
                        onClick={onApplyFilters}
                    />
                </div>
            </aside>
        );
    },
);

export default RmaListFiltersSidebar;
